class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
    this.openerEl = null;
    this._scrollbarWidth = null;
    this._pageHadUserGesture = false;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e)
      );
      return;
    }

    // Always start in a guaranteed closed state on every page load.
    // This runs synchronously before any other JS can fire.
    this._forceClosedState();

    this.init();

    // Only allow openEarly() after the user has actually touched the page.
    const markGesture = () => {
      this._pageHadUserGesture = true;
      document.removeEventListener("pointerdown", markGesture);
    };
    document.addEventListener("pointerdown", markGesture);

    document.addEventListener("click", (e) => {
      if (!this._pageHadUserGesture) return;
      const addToCartBtn = e.target.closest(
        '[name="add"], .add-to-cart, [data-add-to-cart], .btn-add-to-cart'
      );
      if (addToCartBtn && !this.isOpen) {
        this.openEarly();
      }
    });

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        this._onceTransitionEnd(this.drawer, () => {
          setTabindex(this.toggleEelements(), "0");
          this.closeButton().focus({ preventScroll: true });
        });
      }
    });
  }

  // Synchronously force every piece of state to "closed" with no animation.
  // Removes open attribute, active class, body class, scroll lock.
  _forceClosedState() {
    this.isOpen = false;
    this.removeAttribute("open");

    if (this.drawer) {
      this.drawer.classList.remove(this.classDrawerActive);
    }

    this.body.classList.remove(this.activeOverlayBodyClass);
    this.body.style.overflow = "";
    this.body.style.paddingRight = "";

    // Defer tabindex reset slightly so elements exist in DOM
    requestAnimationFrame(() => {
      setTabindex(this.toggleEelements(), "-1");
    });
  }

  openEarly() {
    if (this.isOpen) return;
    this._open();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // Single safe transitionend listener — always self-removes,
  // ignores events from child elements bubbling up.
  _onceTransitionEnd(el, cb) {
    const handler = (e) => {
      if (e.target !== el) return;
      el.removeEventListener("transitionend", handler);
      cb();
    };
    el.removeEventListener("transitionend", handler);
    el.addEventListener("transitionend", handler);
  }

  rememberOpener(e) {
    const current = e?.currentTarget;
    const active = document.activeElement;
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;
    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el || !document.contains(el)) return;
    if (
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true"
    ) return;

    this._onceTransitionEnd(this.drawer, () => {
      el.focus?.({ preventScroll: true });
    });
  }

  getFocusableElements() {
    const sel = "button, [href], input, select, [tabindex]";
    const all = () =>
      Array.from(this.querySelectorAll(sel)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0
      );
    return { focusableElements: all, first: all()[0], last: all()[all().length - 1] };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  // Open the drawer — explicit, not toggled.
  _open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute("open", "");
    this.drawer.classList.add(this.classDrawerActive);
    this.temporaryHideFocusVisible();
    this._lockScroll();

    this._onceTransitionEnd(this.drawer, () => {
      setTabindex(this.toggleEelements(), "0");
      this.closeButton().focus({ preventScroll: true });
    });

    document.dispatchEvent(
      new CustomEvent(PUB_SUB_EVENTS.cartDrawerOpen, { bubbles: true })
    );
  }

  // Close the drawer — explicit, not toggled.
  _close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute("open");
    this.drawer.classList.remove(this.classDrawerActive);
    this.temporaryHideFocusVisible();

    // Unlock scroll only after the animation completes to avoid
    // a layout shift mid-animation on Mac.
    this._onceTransitionEnd(this.drawer, () => {
      setTabindex(this.toggleEelements(), "-1");
      this._unlockScroll();
    });

    this.restoreFocusToOpener();

    document.dispatchEvent(
      new CustomEvent(PUB_SUB_EVENTS.cartDrawerClose, { bubbles: true })
    );
  }

  // toggleDrawerClasses is kept for compatibility with any external callers
  // but now delegates to explicit _open/_close methods.
  toggleDrawerClasses() {
    if (this.isOpen) {
      this._close();
    } else {
      this._open();
    }
  }

  _lockScroll() {
    if (this._scrollbarWidth === null) {
      this._scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
    }
    this.body.style.overflow = "hidden";
    if (this._scrollbarWidth > 0) {
      this.body.style.paddingRight = `${this._scrollbarWidth}px`;
    }
    this.body.classList.add(this.activeOverlayBodyClass);
  }

  _unlockScroll() {
    this.body.style.overflow = "";
    this.body.style.paddingRight = "";
    this.body.classList.remove(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed = e.key === "Tab" || e.keyCode === 9;
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27) {
        if (this.isOpen) this._close();
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus({ preventScroll: true });
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus({ preventScroll: true });
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (!this.isOpen) this.rememberOpener(e);
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this._close();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e)
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    if (isClosedCart && !this.isOpen) {
      this._open();
    }

    requestAnimationFrame(() => {
      this.getSectionsToRender().forEach((section) => {
        const el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (el) {
          el.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );
        }
      });
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`
    )
      .then((r) => r.json())
      .then((response) => {
        this.renderContents({ sections: response }, false);
      })
      .catch((e) => console.log(e));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);